import React from 'react';
import { SecurityAlert } from '../../types/security';
import { securityService } from '../../services/securityService';

interface AlertHistoryProps {
  alerts: SecurityAlert[];
  onAlertAcknowledged?: () => void;
}

const AlertHistory: React.FC<AlertHistoryProps> = ({ alerts, onAlertAcknowledged }) => {
  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return 'bg-red-100 border-red-300 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-300 text-orange-800';
      case 'medium': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'low': return 'bg-blue-100 border-blue-300 text-blue-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return 'text-red-600';
      case 'acknowledged': return 'text-yellow-600';
      case 'resolved': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await securityService.acknowledgeAlert(alertId);
      if (onAlertAcknowledged) {
        onAlertAcknowledged();
      }
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      alert('Failed to acknowledge alert. Please try again.');
    }
  };

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Alert History</h3>
        <div className="text-center text-gray-500 py-8">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No security alerts</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Alert History</h3>
      <div className="space-y-3">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className={`border-l-4 p-4 rounded ${getSeverityColor(alert.severity)}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-semibold">{alert.title}</h4>
                  <span className={`text-xs font-medium uppercase ${getStatusColor(alert.status)}`}>
                    {alert.status}
                  </span>
                </div>
                <p className="text-sm mb-2">{alert.description}</p>
                
                {alert.related_data && Object.keys(alert.related_data).length > 0 && (
                  <div className="text-xs bg-white bg-opacity-50 rounded p-2 mt-2">
                    {Object.entries(alert.related_data).map(([key, value]) => (
                      <div key={key} className="mb-1">
                        <span className="font-medium">{key}:</span> {String(value)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {alert.status === 'active' && (
                <button
                  onClick={() => handleAcknowledge(alert.id)}
                  className="ml-4 px-3 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors"
                >
                  Acknowledge
                </button>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>{new Date(alert.created_at).toLocaleString()}</span>
              {alert.acknowledged_by && (
                <span>Acknowledged by {alert.acknowledged_by}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AlertHistory;
